import React from "react";
import { Shield, Lock, Eye, Trash2, Edit3, UserCheck } from "lucide-react";

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString("en-IN", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const highlights = [
    { icon: <Shield size={20} />, text: "No Data Selling" },
    { icon: <Lock size={20} />, text: "DPDP Act Compliant" },
    { icon: <Trash2 size={20} />, text: "Right to be Forgotten" },
  ];

  return (
    <div className="min-h-screen bg-[#FFF0F3] text-gray-800 font-sans selection:bg-rose-200">
      {/* Decorative Top Bar */}
      <div className="h-2 bg-rose-500 w-full" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header Section */}
        <header className="text-center mb-16">
          <div className="inline-block px-5 py-2 rounded-full bg-rose-100 text-rose-700 text-base font-extrabold mb-6 tracking-wider uppercase shadow-sm">
            Your Privacy • Our Promise
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
            Privacy <span className="text-rose-500">Policy</span>
          </h1>
          <p className="text-xl text-gray-700 font-medium italic mb-6">
            Eveya – A brand of Farmfit Agrihealth LLP
          </p>
          <div className="flex justify-center items-center gap-6 text-base font-medium text-gray-600 flex-wrap">
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Last Updated: {currentDate}
            </span>
            <span className="text-rose-400 hidden sm:inline">•</span>
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Effective Immediately
            </span>
          </div>
        </header>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {highlights.map((item, index) => (
            <div
              key={index}
              className="bg-white p-7 rounded-2xl border border-rose-100 shadow-xl shadow-rose-200/50 flex items-center gap-5 transition-all hover:scale-105 hover:shadow-rose-300/60"
            >
              <div className="p-4 bg-rose-500 rounded-xl text-white flex-shrink-0">
                {item.icon}
              </div>
              <span className="font-bold text-lg text-gray-800">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Main Content Sections */}
        <div className="space-y-10">
          {/* 1. Introduction - Enhanced */}
          <Section 
            title="1. Introduction & Our Commitment" 
            icon={<Eye size={28} className="text-rose-500" />}
          >
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              At <strong className="text-rose-600">Eveya</strong>, your trust and comfort matter more than anything else. 
              This Privacy Policy clearly explains how we — <strong>Eveya, a brand of Farmfit Agrihealth LLP</strong> — 
              collect, use, protect, and (when necessary) share your personal and health-related information.
            </p>
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              We built Eveya to support and empower women through every phase of their cycle — and that mission starts with 
              treating your personal data, especially your sensitive health information, with the highest level of care, 
              confidentiality, and respect.
            </p>

            <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-6 rounded-2xl border border-rose-200 my-8">
              <p className="font-semibold text-rose-700 mb-3">We collect and process your data when you:</p>
              <ul className="grid md:grid-cols-2 gap-3 text-gray-700">
                <li className="flex items-center gap-2"><span className="text-rose-500">•</span> Visit www.eveya.in</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">•</span> Use our mobile app</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">•</span> Make a purchase</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">•</span> Create an account</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">•</span> Subscribe to services</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">•</span> Participate in sponsorships</li>
              </ul>
            </div>

            <p className="text-gray-600 italic text-center mt-4">
              This policy complies fully with the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> 
              and prioritizes the protection of your sensitive personal data.
            </p>
          </Section>

          {/* 2. Information we collect */}
          <Section 
            title="2. Information We Collect" 
            icon={<UserCheck size={28} className="text-rose-500" />}
          >
            <h3 className="font-bold text-xl text-gray-900 mt-2 mb-4 border-b border-rose-100 pb-2">
              2.1 Information You Provide Directly
            </h3>
            <p className="font-semibold text-gray-800">A. Account Registration:</p>
            <ul className="list-disc pl-6 space-y-1.5 mt-2">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Date of birth (to verify age 18+)</li>
              <li>Gender identity (optional; for personalization)</li>
              <li>Password (encrypted)</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-5">B. Delivery & Billing Information:</p>
            <ul className="list-disc pl-6 space-y-1.5 mt-2">
              <li>Home address</li>
              <li>Billing address (if different)</li>
              <li>Delivery preferences</li>
              <li>Payment method (card details processed by payment gateway; we don't store full card numbers)</li>
            </ul>

            <div className="mt-5 bg-rose-50 p-5 rounded-xl border-l-4 border-rose-500">
              <p className="font-semibold text-gray-800">C. Health & Preference Information:</p>
              <p className="text-sm text-rose-700 mt-1 mb-3">
                (IMPORTANT: This is SENSITIVE PERSONAL DATA under DPDP Act. We handle this with highest confidentiality.)
              </p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Menstrual cycle length/regularity (optional - for product recommendations)</li>
                <li>Product allergies or skin sensitivities</li>
                <li>Preferred product types (cramp reduction pads, period panties, etc.)</li>
                <li>Special requirements or medical conditions you disclose</li>
              </ul>
            </div>

            <p className="font-semibold text-gray-800 mt-5">D. Customer Service & Communication:</p>
            <ul className="list-disc pl-6 space-y-1.5 mt-2">
              <li>Emails or messages you send us</li>
              <li>Support chat conversations</li>
              <li>Feedback and reviews</li>
              <li>Survey responses</li>
            </ul>

            <h3 className="font-bold text-xl text-gray-900 mt-10 mb-4 border-b border-rose-100 pb-2">
              2.2 Information Collected Automatically
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Website/App Usage:</strong> Pages visited, time spent, products viewed, clicks, search queries, cart behavior</li>
              <li><strong>Device Information:</strong> Device type, OS, browser, IP address, city-level location</li>
              <li><strong>Cookies & Tracking:</strong> Session, persistent, analytics, marketing cookies (you can disable in browser settings)</li>
              <li><strong>Analytics Services:</strong> Google Analytics (IP anonymized)</li>
            </ul>

            <h3 className="font-bold text-xl text-gray-900 mt-10 mb-4 border-b border-rose-100 pb-2">
              2.3 Information from Third Parties
            </h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Payment gateways – transaction status only</li>
              <li>Delivery partners – tracking updates</li>
              <li>Corporate/university partners – aggregate data only (with consent)</li>
              <li>Social login (Facebook/Google) – public profile info only</li>
            </ul>

            <h3 className="font-bold text-xl text-gray-900 mt-10 mb-4 border-b border-rose-100 pb-2">
              2.4 Information We Do NOT Collect
            </h3>
            <ul className="list-disc pl-6 space-y-1.5 text-rose-700 font-medium">
              <li>Aadhaar, PAN, government IDs</li>
              <li>Biometric data</li>
              <li>Camera/microphone/location without permission</li>
              <li>Cross-site third-party tracking</li>
            </ul>
          </Section>

          {/* 3. How we use your information */}
          <Section title="3. How We Use Your Information">
            <ul className="list-disc pl-6 space-y-3 text-gray-700">
              <li><strong>3.1 Providing Services:</strong> Orders, subscriptions, tracking, support, refunds</li>
              <li><strong>3.2 Personalization:</strong> Product recommendations, content suggestions</li>
              <li><strong>3.3 Marketing & Communication:</strong> Emails, SMS, WhatsApp, push (opt-out available)</li>
              <li><strong>3.4 Compliance & Legal:</strong> Age verification, GST, fraud prevention, legal requests</li>
              <li><strong>3.5 Analytics & Improvement:</strong> Traffic analysis, A/B testing, UX improvements</li>
              <li><strong>3.6 Research & Development:</strong> Anonymized insights for new products</li>
              <li><strong>3.7 Fraud Prevention:</strong> Detect suspicious activity</li>
              <li className="bg-rose-50 p-4 rounded-lg">
                <strong>3.8 HEALTH DATA - Special Protections:</strong><br />
                Used ONLY for recommendations, content, anonymized research.<br />
                <strong className="text-rose-700">Never sold, shared with insurers/employers/family without consent.</strong>
              </li>
              <li><strong>3.9 CORPORATE/B2B:</strong> Only aggregate anonymized reports to employers</li>
            </ul>
          </Section>

          {/* 4. Data Sharing */}
          <Section title="4. Data Sharing and Third Parties">
            <p className="font-bold text-lg mb-3 text-gray-900">4.1 We DO NOT Sell Your Data</p>
            <p className="mb-4"><strong>4.2 When We Share Data:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Providers:</strong> Payment gateways (Razorpay, PhonePe…), couriers, email (Mailchimp…), cloud (AWS…)</li>
              <li><strong>Business Transfers:</strong> In case of merger/acquisition (with notice)</li>
              <li><strong>Legal Obligations:</strong> Court/government requests</li>
              <li><strong>With Consent:</strong> Reviews, research participation, doctor sharing</li>
            </ul>
            <p className="mt-6"><strong>4.4 International Data Transfer:</strong> Allowed with safeguards (e.g. AWS Singapore/US with encryption)</p>
          </Section>

          {/* 5. Data Security */}
          <Section 
            title="5. Data Security and How We Store Data" 
            icon={<Lock size={28} className="text-rose-500" />}
          >
            <p><strong>5.1 Security Measures:</strong> HTTPS/TLS, AES-256 at rest, firewalls, MFA, audits, incident response (72h notice per DPDP)</p>
            <p className="mt-4"><strong>5.2 Payment:</strong> No full card storage – only last-4 digits & confirmation</p>
            <p className="mt-3"><strong>5.3 Passwords:</strong> bcrypt hashed – never stored plain</p>

            <div className="overflow-x-auto mt-8">
              <table className="w-full text-sm text-left border-collapse">
                <thead className="bg-rose-100 text-rose-700">
                  <tr>
                    <th className="p-4 border-b border-rose-200 font-semibold">Data Type</th>
                    <th className="p-4 border-b border-rose-200 font-semibold">Retention Period</th>
                    <th className="p-4 border-b border-rose-200 font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-100">
                  <tr><td className="p-4">Account info</td><td className="p-4">Until deleted + 90 days</td><td className="p-4">Dispute resolution</td></tr>
                  <tr><td className="p-4">Order & transaction records</td><td className="p-4">6 years</td><td className="p-4">GST/tax law</td></tr>
                  <tr><td className="p-4">Payment confirmations</td><td className="p-4">7 years</td><td className="p-4">RBI regulations</td></tr>
                  <tr><td className="p-4">Health/preference data</td><td className="p-4">2 years after last order</td><td className="p-4">Recommendations & disputes</td></tr>
                  <tr><td className="p-4">Website analytics</td><td className="p-4">26 months</td><td className="p-4">Google default</td></tr>
                  <tr><td className="p-4">Support chats</td><td className="p-4">1 year</td><td className="p-4">Service records</td></tr>
                  <tr><td className="p-4">Marketing emails </td><td className="p-4">3 months</td><td className="p-4">Compliance with NISM guidelines</td></tr>
                  <tr><td className="p-4">Support chats</td><td className="p-4">90 days</td><td className="p-4">Allow for recovery requests</td></tr>

                </tbody>
              </table>
            </div>

            <p className="mt-3"><strong>5.5 Data Minimization:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We collect ONLY data necessary to serve you.</li>
              <li>We do NOT ask for information that's not required.</li>
              <li>Annual privacy audits ensure we're not over-collecting.</li>
            </ul>

            <p className="mt-3"><strong>5.6 Security Limitations:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>While we use industry-standard security, no system is 100% secure.</li>
              <li>We are NOT liable for unauthorized access due to your account sharing, weak passwords, or your device being compromised.</li>
              <li>If you suspect unauthorized access, change your password immediately and contact support.</li>
            </ul>
          </Section>

          {/* 6. Your Rights */}
          <Section 
            title="6. Your Rights Under DPDP Act & Consumer Protection Act" 
            icon={<Edit3 size={28} className="text-rose-500" />}
          >
            <div className="grid md:grid-cols-2 gap-5 mt-6">
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2">Right to Access</h4>
                <p className="text-sm text-gray-600">Request copy of your data (free once/year)</p>
              </div>
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2">Right to Correction</h4>
                <p className="text-sm text-gray-600">Fix inaccurate information</p>
              </div>
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2">Right to Deletion</h4>
                <p className="text-sm text-gray-600">Right to be Forgotten (subject to legal retention)</p>
              </div>
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2">Right to Withdraw Consent</h4>
                <p className="text-sm text-gray-600">Stop marketing & some processing</p>
              </div>
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2">Right to Portability</h4>
                <p className="text-sm text-gray-600">Get data in CSV/JSON</p>
              </div>
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2">Opt-out of Marketing</h4>
                <p className="text-sm text-gray-600">Unsubscribe anytime</p>
              </div>
            </div>

            <p className="mt-10 text-center font-medium text-gray-700">
              Send requests to: <a href="mailto:support@eveya.in" className="text-rose-600 underline hover:text-rose-700">support@eveya.in</a>
              <br />
              <span className="text-sm">(Subject examples: "Data Access Request", "Data Deletion Request")</span>
            </p>
          </Section>

          {/* 7. Cookies */}
          <Section title="7. Cookies, Tracking and Advertising">
            <p className="text-gray-700">We use cookies to improve experience, analytics & marketing.</p>
            <p className="mt-4 text-gray-700">
              You can manage/disable cookies in your browser. Third-party opt-outs:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-gray-600">
              <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" className="text-rose-600 hover:underline">https://tools.google.com/dlpage/gaoptout</a></li>
              <li>Facebook Ads: <a href="https://facebook.com/ads/preferences" className="text-rose-600 hover:underline">https://facebook.com/ads/preferences</a></li>
            </ul>
          </Section>

          {/* 8 & 9 */}
          <Section title="8. Children’s Privacy & 9. Changes to this Policy">
            <p className="text-gray-700">
              <strong>8. Children’s Privacy:</strong> Services are strictly 18+ only. We do not knowingly collect data from children under 18.
            </p>
            <p className="mt-5 text-gray-700">
              <strong>9. Changes to this Policy:</strong> We may update this policy from time to time. Material changes will be notified via email or prominent notice on the site. Continued use after changes constitutes acceptance.
            </p>
          </Section>
        </div>

        {/* Footer */}
        {/* <footer className="mt-24 text-center text-gray-600 text-sm pb-12">
          <p className="font-medium">© {new Date().getFullYear()} Farmfit Agrihealth LLP. All rights reserved.</p>
          <p className="mt-4 uppercase tracking-widest font-bold text-xs text-rose-600">
            Eveya – Wellness. Privacy. Transparency.
          </p>
        </footer> */}
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <section className="bg-white rounded-3xl p-9 border border-rose-100 shadow-xl shadow-rose-200/40 hover:shadow-rose-300/50 transition-all duration-300">
    <div className="flex items-center gap-5 mb-7">
      {icon && (
        <div className="p-3.5 bg-gradient-to-br from-rose-50 to-rose-100 rounded-2xl shadow-sm">
          {icon}
        </div>
      )}
      <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="space-y-5 text-gray-700 leading-relaxed text-[15.5px]">
      {children}
    </div>
  </section>
);

export default PrivacyPolicy;